import React, { useState, useEffect } from "react";
import { Row, Col, Card, Badge, Spinner, Button, Tab, Nav } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageTitle from "../../layouts/PageTitle";
import usePermission from "../../../hooks/usePermission";
import {
 getEquipment,
 getDeploymentLogs,
} from "../../../services/equipmentService";

const STATUS_COLORS = {
 Available: "success",
 Deployed: "primary",
 "Under Maintenance": "warning",
 Idle: "secondary",
 Retired: "dark",
 "In Transit": "info",
};

const MAINTENANCE_COLORS = {
 Good: "success",
 "Due Soon": "warning",
 Overdue: "danger",
 "Under Repair": "danger",
 "N/A": "secondary",
};

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "—");

const EquipmentView = () => {
 const navigate = useNavigate();
 const { id } = useParams();
 const { hasPermission } = usePermission();
 const canManage = hasPermission(["create:equipment", "edit:equipment", "delete:equipment"]);

 const [equipment, setEquipment] = useState(null);
 const [logs, setLogs] = useState([]);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 const load = async () => {
 try {
 const [eRes, lRes] = await Promise.all([
 getEquipment(id),
 getDeploymentLogs({ equipment: id, limit: 50 }),
 ]);
 setEquipment(eRes.data?.data);
 setLogs(lRes.data?.data || []);
 } catch (err) {
 toast.error("Failed to load equipment");
 } finally {
 setLoading(false);
 }
 };
 load();
 }, [id]);

 if (loading) {
 return (
 <div className="text-center py-5">
 <Spinner animation="border" variant="primary" />
 </div>
 );
 }

 if (!equipment) {
 return (
 <div className="text-center py-5 text-muted">
 Equipment not found.
 </div>
 );
 }

 const e = equipment;

 return (
 <>
 <PageTitle activeMenu={e.name} motherMenu="Equipment" />

 {/* Header */}
 <Card className="shadow-sm mb-3">
 <Card.Body>
 <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
 <div>
 <div className="d-flex align-items-center gap-3 flex-wrap">
 <h4 className="mb-1 fw-bold">{e.name}</h4>
 <Badge bg={STATUS_COLORS[e.status] || "secondary"} className="fs-6 px-3">
 {e.status}
 </Badge>
 <Badge bg={MAINTENANCE_COLORS[e.maintenanceStatus] || "secondary"}>
 Maintenance: {e.maintenanceStatus}
 </Badge>
 </div>
 <p className="text-muted mb-0 mt-1">
 <span className="badge bg-light text-dark border me-2">{e.equipmentCode}</span>
 {e.category}
 {e.type && ` · ${e.type}`}
 {e.make && ` · ${e.make}`}
 {e.model && ` ${e.model}`}
 </p>
 </div>
 <div className="d-flex gap-2">
 {canManage && (
 <>
 <Button variant="outline-primary" size="sm" onClick={() => navigate(`../equipment/edit/${id}`)}>
 <i className="fas fa-edit me-1" />Edit
 </Button>
 {e.status !== "Deployed" && (
 <Button variant="primary" size="sm" onClick={() => navigate(`../equipment/deploy/${id}`)}>
 <i className="fas fa-truck me-1" />Deploy
 </Button>
 )}
 {e.status === "Deployed" && (
 <Button variant="success" size="sm" onClick={() => navigate(`../equipment/return/${id}`)}>
 <i className="fas fa-undo me-1" />Return
 </Button>
 )}
 </>
 )}
 <Button variant="outline-secondary" size="sm" onClick={() => navigate("../equipment")}>
 <i className="fas fa-arrow-left me-1" />Back
 </Button>
 </div>
 </div>
 </Card.Body>
 </Card>

 {/* Utilization Summary */}
 <Row className="mb-3">
 {[
 { label: "Total Utilization", value: `${e.totalUtilizationHours || 0} hrs`, icon: "fa-clock", color: "primary" },
 { label: "Current Deployment", value: `${e.currentDeploymentHours || 0} hrs`, icon: "fa-play-circle", color: "info" },
 { label: "Deployed From", value: fmt(e.deployedFrom), icon: "fa-calendar-check", color: "success" },
 { label: "Next Maintenance", value: fmt(e.nextMaintenanceDate), icon: "fa-tools", color: "warning" },
 ].map((s) => (
 <Col key={s.label} xs={6} md={3} className="mb-3">
 <Card className={`border-${s.color} border-start border-4 shadow-sm`}>
 <Card.Body className="py-3">
 <div className="d-flex align-items-center gap-3">
 <i className={`fas ${s.icon} fa-2x text-${s.color}`} />
 <div>
 <p className="mb-0 text-muted small">{s.label}</p>
 <strong>{s.value}</strong>
 </div>
 </div>
 </Card.Body>
 </Card>
 </Col>
 ))}
 </Row>

 {/* Tabs */}
 <Card className="shadow-sm">
 <Card.Body>
 <Tab.Container defaultActiveKey="details">
 <Nav variant="tabs" className="mb-3">
 <Nav.Item>
 <Nav.Link eventKey="details">
 <i className="fas fa-info-circle me-1" />Details
 </Nav.Link>
 </Nav.Item>
 <Nav.Item>
 <Nav.Link eventKey="deployment">
 <i className="fas fa-history me-1" />Deployment Log
 {logs.length > 0 && (
 <Badge bg="primary" className="ms-2">{logs.length}</Badge>
 )}
 </Nav.Link>
 </Nav.Item>
 </Nav>

 <Tab.Content>
 {/* Details Tab */}
 <Tab.Pane eventKey="details">
 <Row>
 <Col md={6}>
 <table className="table table-borderless table-sm">
 <tbody>
 {[
 ["Equipment Code", e.equipmentCode],
 ["Category", e.category],
 ["Type", e.type || "—"],
 ["Make", e.make || "—"],
 ["Model", e.model || "—"],
 ["Serial Number", e.serialNumber || "—"],
 ["Year of Manufacture", e.yearOfManufacture || "—"],
 ].map(([label, value]) => (
 <tr key={label}>
 <td className="text-muted fw-semibold" style={{ width: "45%" }}>{label}</td>
 <td>{value}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </Col>
 <Col md={6}>
 <table className="table table-borderless table-sm">
 <tbody>
 {[
 ["Current Status", <Badge key="s" bg={STATUS_COLORS[e.status] || "secondary"}>{e.status}</Badge>],
 ["Project", e.project?.projectName || "—"],
 ["Work Order", e.workOrder?.woNo || "—"],
 ["Operator", e.operator?.name || "—"],
 ["Location", e.location || "—"],
 ["Last Maintenance", fmt(e.lastMaintenanceDate)],
 ["Next Maintenance", fmt(e.nextMaintenanceDate)],
 ].map(([label, value]) => (
 <tr key={label}>
 <td className="text-muted fw-semibold" style={{ width: "45%" }}>{label}</td>
 <td>{value}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </Col>
 </Row>
 {e.remarks && (
 <div className="mt-2 p-3 bg-light rounded">
 <strong>Remarks:</strong>
 <p className="mb-0 mt-1">{e.remarks}</p>
 </div>
 )}
 </Tab.Pane>

 {/* Deployment Log Tab */}
 <Tab.Pane eventKey="deployment">
 {logs.length === 0 ? (
 <div className="text-center py-5 text-muted">
 <i className="fas fa-history fa-3x mb-3 d-block" />
 No deployment history
 </div>
 ) : (
 <div className="table-responsive">
 <table className="table table-hover align-middle">
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>Project</th>
 <th>Work Order</th>
 <th>Operator</th>
 <th>Location</th>
 <th>Deployed From</th>
 <th>Returned On</th>
 <th>Hours</th>
 <th>Status</th>
 <th>Purpose</th>
 </tr>
 </thead>
 <tbody>
 {logs.map((log, idx) => (
 <tr key={log._id}>
 <td className="text-muted">{idx + 1}</td>
 <td>{log.project?.projectName || "—"}</td>
 <td>{log.workOrder?.woNo || "—"}</td>
 <td>{log.operator?.name || "—"}</td>
 <td>{log.location || "—"}</td>
 <td>{fmt(log.deployedFrom)}</td>
 <td>{log.deployedTo ? fmt(log.deployedTo) : <Badge bg="primary">Active</Badge>}</td>
 <td><strong>{log.utilizationHours || 0} h</strong></td>
 <td>
 <Badge bg={log.status === "Active" ? "primary" : log.status === "Returned" ? "success" : "info"}>
 {log.status}
 </Badge>
 </td>
 <td>{log.purpose || "—"}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </Tab.Pane>
 </Tab.Content>
 </Tab.Container>
 </Card.Body>
 </Card>
 </>
 );
};

export default EquipmentView;
